import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { DsIconGlyph } from '@/ds/icon'
import type { DsIcon } from '@/ds/icons'

export interface DsChoiceCardProps<T extends string> {
  value: T
  groupValue: T | null
  onChange: (value: T) => void
  title: string
  description?: string
  icon?: DsIcon
  trailing?: ReactNode
  disabled?: boolean
  className?: string
}

export function DsChoiceCard<T extends string>({ value, groupValue, onChange, title, description, icon, trailing, disabled, className }: DsChoiceCardProps<T>) {
  const selected = value === groupValue
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={() => onChange(value)}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border bg-card p-4 text-left outline-none transition-colors motion-fast focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-60',
        selected ? 'border-brand/60 bg-accent' : 'border-border hover:bg-muted',
        className,
      )}
    >
      {icon && (
        <span className={cn('inline-flex size-10 shrink-0 items-center justify-center rounded-lg', selected ? 'bg-brand-vivid/20 text-accent-foreground' : 'bg-muted text-muted-foreground')}>
          <DsIconGlyph icon={icon} size={20} />
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={cn('text-body-strong', selected ? 'text-accent-foreground' : 'text-foreground')}>{title}</span>
        {description && <span className="text-small text-muted-foreground">{description}</span>}
      </span>
      {trailing}
      <span aria-hidden className={cn('size-[22px] shrink-0 rounded-full', selected ? 'border-[6px] border-primary bg-card' : 'border-[1.5px] border-input bg-card')} />
    </button>
  )
}
